import { z } from 'zod'

// =====================
// Pay Period Enums
// =====================

export enum PayPeriodTypeEnum {
  WEEKLY = 'WEEKLY',
  BIWEEKLY = 'BIWEEKLY',
  SEMIMONTHLY = 'SEMIMONTHLY',
  MONTHLY = 'MONTHLY',
}

export enum PayPeriodStatusEnum {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  LOCKED = 'LOCKED',
}

export enum UserPayPeriodStatusEnum {
  OPEN = 'OPEN',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  CLOSED = 'CLOSED',
}

// =====================
// Pay Period Schemas
// =====================

// User subset for pay period actions
const PayPeriodUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
})

// Pay period schema
export const PayPeriodSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  name: z.string(), // "Dec 9-15, 2024" or "Dec 1-15, 2024"
  periodType: z.nativeEnum(PayPeriodTypeEnum),
  startDate: z.date(),
  endDate: z.date(),
  status: z.nativeEnum(PayPeriodStatusEnum).default(PayPeriodStatusEnum.OPEN),
  closedAt: z.date().nullable().optional(),
  closedBy: z.string().uuid().nullable().optional(),
  closedByUser: PayPeriodUserSchema.optional(),
  lockedAt: z.date().nullable().optional(),
  lockedBy: z.string().uuid().nullable().optional(),
  lockedByUser: PayPeriodUserSchema.optional(),
  // Summary stats (calculated)
  totalHours: z.number().nullable().optional(),
  billableHours: z.number().nullable().optional(),
  approvedHours: z.number().nullable().optional(),
  pendingHours: z.number().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export type PayPeriod = z.infer<typeof PayPeriodSchema>

// Create pay period schema
export const CreatePayPeriodSchema = PayPeriodSchema.omit({
  id: true,
  organizationId: true,
  closedAt: true,
  closedBy: true,
  closedByUser: true,
  lockedAt: true,
  lockedBy: true,
  lockedByUser: true,
  totalHours: true,
  billableHours: true,
  approvedHours: true,
  pendingHours: true,
  createdAt: true,
  updatedAt: true,
})

export type CreatePayPeriod = z.infer<typeof CreatePayPeriodSchema>

// Update pay period schema
export const UpdatePayPeriodSchema = PayPeriodSchema.omit({
  id: true,
  organizationId: true,
  closedByUser: true,
  lockedByUser: true,
  createdAt: true,
  updatedAt: true,
}).partial()

export type UpdatePayPeriod = z.infer<typeof UpdatePayPeriodSchema>

// Pay period response schema
export const PayPeriodResponseSchema = z.object({
  data: PayPeriodSchema,
  message: z.string().optional(),
})

export type PayPeriodResponse = z.infer<typeof PayPeriodResponseSchema>

// Pay periods list response schema
export const PayPeriodsResponseSchema = z.object({
  data: z.array(PayPeriodSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type PayPeriodsResponse = z.infer<typeof PayPeriodsResponseSchema>

// =====================
// Pay Period Summary Schemas
// =====================

// Member summary within a pay period
export const PayPeriodMemberSummarySchema = z.object({
  memberId: z.string().uuid(),
  userId: z.string().uuid(),
  memberName: z.string(),
  memberEmail: z.string(),
  memberImage: z.string().nullable().optional(),
  totalHours: z.number(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  draftHours: z.number(),
  submittedHours: z.number(),
  approvedHours: z.number(),
  rejectedHours: z.number(),
  changesRequestedHours: z.number(),
  entryCount: z.number(),
  expectedHours: z.number().nullable().optional(), // Based on member capacity
  utilizationPercent: z.number().nullable().optional(),
  // User pay period tracking
  userPayPeriodId: z.string().uuid().optional(),
  userPayPeriodStatus: z.nativeEnum(UserPayPeriodStatusEnum).optional(),
})

export type PayPeriodMemberSummary = z.infer<
  typeof PayPeriodMemberSummarySchema
>

// Project summary within a pay period
export const PayPeriodProjectSummarySchema = z.object({
  projectId: z.string().uuid().nullable(),
  projectName: z.string().nullable(),
  totalHours: z.number(),
  billableHours: z.number(),
  nonBillableHours: z.number(),
  memberCount: z.number(),
})

export type PayPeriodProjectSummary = z.infer<
  typeof PayPeriodProjectSummarySchema
>

// Full pay period summary
export const PayPeriodSummarySchema = z.object({
  payPeriod: PayPeriodSchema,
  memberSummaries: z.array(PayPeriodMemberSummarySchema),
  projectSummaries: z.array(PayPeriodProjectSummarySchema),
  totals: z.object({
    totalHours: z.number(),
    billableHours: z.number(),
    nonBillableHours: z.number(),
    draftHours: z.number(),
    submittedHours: z.number(),
    approvedHours: z.number(),
    rejectedHours: z.number(),
    changesRequestedHours: z.number(),
    memberCount: z.number(),
    entryCount: z.number(),
  }),
})

export type PayPeriodSummary = z.infer<typeof PayPeriodSummarySchema>

export const PayPeriodSummaryResponseSchema = z.object({
  data: PayPeriodSummarySchema,
  message: z.string().optional(),
})

export type PayPeriodSummaryResponse = z.infer<
  typeof PayPeriodSummaryResponseSchema
>

// =====================
// Missing Timesheets Schema
// =====================

export const MissingTimesheetSchema = z.object({
  memberId: z.string().uuid(),
  userId: z.string().uuid(),
  memberName: z.string(),
  memberEmail: z.string(),
  memberImage: z.string().nullable().optional(),
  expectedHours: z.number(),
  loggedHours: z.number(),
  missingHours: z.number(),
  hasAnyEntries: z.boolean(),
  lastEntryDate: z.date().nullable().optional(),
})

export type MissingTimesheet = z.infer<typeof MissingTimesheetSchema>

export const MissingTimesheetsResponseSchema = z.object({
  data: z.array(MissingTimesheetSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type MissingTimesheetsResponse = z.infer<
  typeof MissingTimesheetsResponseSchema
>

// =====================
// All Users Timesheet Row (for admin "All Timesheets" view)
// =====================

export const AllUsersTimesheetRowSchema = z.object({
  userId: z.string().uuid(),
  user: PayPeriodUserSchema,
  payPeriodId: z.string().uuid(),
  payPeriodName: z.string(),
  periodType: z.nativeEnum(PayPeriodTypeEnum),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  payPeriodStatus: z.nativeEnum(PayPeriodStatusEnum),
  userPayPeriodStatus: z.nativeEnum(UserPayPeriodStatusEnum),
  totalHours: z.number(),
  billableHours: z.number(),
  entryStatusBreakdown: z.object({
    draft: z.number(),
    submitted: z.number(),
    approved: z.number(),
    rejected: z.number(),
    changesRequested: z.number(),
  }),
  submittedEntryIds: z.array(z.string().uuid()),
  draftEntryIds: z.array(z.string().uuid()),
  allEntryIds: z.array(z.string().uuid()),
  // Submitted entries that are billable but carry no bill rate. Approving
  // freezes them at $0 for good — no rate change or backfill reaches an
  // APPROVED row — so the bulk approve dialog warns before it happens.
  unratedSubmittedEntryIds: z.array(z.string().uuid()).optional(),
  submissionIssueCount: z.number().optional(),
  // True when the member has overlapping (double-tracked) time entries in the
  // period. Surfaced as a warning badge on the All Timesheets row.
  hasTimeConflicts: z.boolean().optional(),
})

export type AllUsersTimesheetRow = z.infer<typeof AllUsersTimesheetRowSchema>

// =====================
// Pay Period Timesheet Summary (lightweight, for listing pay periods without user details)
// =====================

export const PayPeriodTimesheetSummarySchema = z.object({
  payPeriodId: z.string().uuid(),
  payPeriodName: z.string(),
  periodType: z.nativeEnum(PayPeriodTypeEnum),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  payPeriodStatus: z.nativeEnum(PayPeriodStatusEnum),
  memberCount: z.number(),
  totalHours: z.number(),
  billableHours: z.number(),
  entryStatusBreakdown: z.object({
    draft: z.number(),
    submitted: z.number(),
    approved: z.number(),
    rejected: z.number(),
    changesRequested: z.number(),
  }),
  userStatusBreakdown: z.object({
    open: z.number(),
    submitted: z.number(),
    approved: z.number(),
    closed: z.number(),
  }),
})

export type PayPeriodTimesheetSummary = z.infer<
  typeof PayPeriodTimesheetSummarySchema
>

// =====================
// Generate Pay Periods Request
// =====================

export const GeneratePayPeriodsRequestSchema = z.object({
  startDate: z.string(), // ISO date string
  endDate: z.string(), // ISO date string
  periodType: z.nativeEnum(PayPeriodTypeEnum).optional(), // Use org default if not specified
})

export type GeneratePayPeriodsRequest = z.infer<
  typeof GeneratePayPeriodsRequestSchema
>

export const GeneratePayPeriodsResponseSchema = z.object({
  data: z.array(PayPeriodSchema),
  created: z.number(),
  skipped: z.number(), // Already existing periods
  message: z.string().optional(),
})

export type GeneratePayPeriodsResponse = z.infer<
  typeof GeneratePayPeriodsResponseSchema
>

// =====================
// Pay Period Utility Types
// =====================

// Date range for pay period calculations
export interface PayPeriodDateRange {
  startDate: Date
  endDate: Date
  name: string
}

// Pay period configuration from org settings
export interface PayPeriodConfig {
  periodType: PayPeriodTypeEnum
  startDay: number // 0-6 for weekly, day of month for monthly
  firstPayPeriodDate?: string // ISO date for biweekly alignment
  semiMonthlyFirstDay: number
  semiMonthlySecondDay: number
}

// =====================
// User Pay Period Schemas
// =====================

export const UserPayPeriodSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
  payPeriodId: z.string().uuid(),
  status: z.nativeEnum(UserPayPeriodStatusEnum),
  periodType: z.nativeEnum(PayPeriodTypeEnum),
  submittedAt: z.date().nullable().optional(),
  submittedBy: z.string().uuid().nullable().optional(),
  approvedAt: z.date().nullable().optional(),
  approvedBy: z.string().uuid().nullable().optional(),
  closedAt: z.date().nullable().optional(),
  closedBy: z.string().uuid().nullable().optional(),
  isCustomAssignment: z.boolean().default(false),
  totalHours: z.number().nullable().optional(),
  billableHours: z.number().nullable().optional(),
  entryStatusBreakdown: z
    .object({
      draft: z.number(),
      submitted: z.number(),
      approved: z.number(),
      rejected: z.number(),
      changesRequested: z.number(),
    })
    .optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  // Joined data (included when fetching with relations)
  payPeriod: PayPeriodSchema.optional(),
  user: PayPeriodUserSchema.optional(),
})

export type UserPayPeriod = z.infer<typeof UserPayPeriodSchema>

// Response schemas
export const UserPayPeriodResponseSchema = z.object({
  data: UserPayPeriodSchema,
  message: z.string().optional(),
})

export type UserPayPeriodResponse = z.infer<typeof UserPayPeriodResponseSchema>

export const UserPayPeriodsResponseSchema = z.object({
  data: z.array(UserPayPeriodSchema),
  total: z.number().optional(),
  message: z.string().optional(),
})

export type UserPayPeriodsResponse = z.infer<
  typeof UserPayPeriodsResponseSchema
>
